import { jsx as _jsx } from "react/jsx-runtime";
/**
 * AuthContext - Manages authentication state and user sessions
 * Provides authentication functionality throughout the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { supabasePermissionsService } from '../services/supabase-permissions.service';
import { useTeamStore } from '../store/team.store';
export const AuthContext = createContext(undefined);
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEcosystemAdmin, setIsEcosystemAdmin] = useState(false);
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
    // Get loadTeams function from team store
    const { loadTeams } = useTeamStore();
    // Load initial session
    useEffect(() => {
        let mounted = true;
        async function loadSession() {
            try {
                console.log('🔄 AuthContext: Loading session...');
                // Get current session
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                if (!mounted)
                    return;
                if (currentSession) {
                    console.log('✅ AuthContext: Session found, loading profile...');
                    setSession(currentSession);
                    setUser(currentSession.user);
                    try {
                        // Load user profile with 30s timeout (multiple DB calls needed)
                        const profilePromise = supabasePermissionsService.getCurrentUserProfile();
                        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Profile loading timeout')), 30000));
                        const profile = await Promise.race([profilePromise, timeoutPromise]);
                        if (!mounted)
                            return;
                        console.log('✅ AuthContext: Profile loaded', profile);
                        // Auto-assign to first organization if user has none
                        if (profile && !profile.organization_id) {
                            console.log('⚠️ User has no organization, auto-assigning to first available...');
                            try {
                                const userOrgs = await supabasePermissionsService.getUserOrganizations(profile.id);
                                if (userOrgs && userOrgs.length > 0) {
                                    const firstOrg = userOrgs[0];
                                    await supabasePermissionsService.updateUserProfile(profile.id, {
                                        organization_id: firstOrg.id
                                    });
                                    profile.organization_id = firstOrg.id;
                                    console.log('✅ Auto-assigned to organization:', firstOrg.name);
                                }
                            }
                            catch (error) {
                                console.error('Failed to auto-assign organization:', error);
                            }
                        }
                        setUserProfile(profile);
                        // Load teams for user's organization
                        if (profile?.organization_id) {
                            console.log('✅ AuthContext: Loading teams for organization:', profile.organization_id);
                            loadTeams(profile.organization_id);
                        }
                        // Check admin roles in parallel
                        if (profile) {
                            const [ecosystem, platform] = await Promise.all([
                                supabasePermissionsService.isEcosystemAdmin(profile.id),
                                supabasePermissionsService.isPlatformAdmin(profile.id)
                            ]);
                            if (!mounted)
                                return;
                            setIsEcosystemAdmin(ecosystem);
                            setIsPlatformAdmin(platform);
                        }
                    }
                    catch (profileError) {
                        console.error('⚠️ AuthContext: Error loading profile (continuing anyway):', profileError);
                        // Continue without profile - don't block login
                        if (mounted) {
                            setUserProfile(null);
                        }
                    }
                }
                else {
                    console.log('ℹ️ AuthContext: No session found');
                }
            }
            catch (error) {
                console.error('❌ AuthContext: Error loading session:', error);
            }
            finally {
                if (mounted) {
                    console.log('✅ AuthContext: Loading complete');
                    setLoading(false);
                }
            }
        }
        loadSession();
        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
            console.log('🔔 Auth state changed:', event);
            setSession(currentSession);
            setUser(currentSession?.user ?? null);
            if (currentSession?.user) {
                console.log('✅ Auth change: User signed in, loading profile...');
                try {
                    // Reload user profile with 10s timeout
                    const profilePromise = supabasePermissionsService.getCurrentUserProfile();
                    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Profile loading timeout')), 10000));
                    const profile = await Promise.race([profilePromise, timeoutPromise]);
                    console.log('✅ Auth change: Profile loaded', profile);
                    // Auto-assign to first organization if user has none
                    if (profile && !profile.organization_id) {
                        console.log('⚠️ User has no organization, auto-assigning to first available...');
                        try {
                            const userOrgs = await supabasePermissionsService.getUserOrganizations(profile.id);
                            if (userOrgs && userOrgs.length > 0) {
                                const firstOrg = userOrgs[0];
                                await supabasePermissionsService.updateUserProfile(profile.id, {
                                    organization_id: firstOrg.id
                                });
                                profile.organization_id = firstOrg.id;
                                console.log('✅ Auto-assigned to organization:', firstOrg.name);
                            }
                        }
                        catch (error) {
                            console.error('Failed to auto-assign organization:', error);
                        }
                    }
                    setUserProfile(profile);
                    // Load teams for user's organization
                    if (profile?.organization_id) {
                        console.log('✅ Auth change: Loading teams for organization:', profile.organization_id);
                        loadTeams(profile.organization_id);
                    }
                    if (profile) {
                        const [ecosystem, platform] = await Promise.all([
                            supabasePermissionsService.isEcosystemAdmin(profile.id),
                            supabasePermissionsService.isPlatformAdmin(profile.id)
                        ]);
                        setIsEcosystemAdmin(ecosystem);
                        setIsPlatformAdmin(platform);
                    }
                }
                catch (error) {
                    console.error('⚠️ Auth change: Error loading profile (continuing anyway):', error);
                    // Continue without profile - don't block login
                    setUserProfile(null);
                }
            }
            else {
                console.log('ℹ️ Auth change: User signed out');
                setUserProfile(null);
                setIsEcosystemAdmin(false);
                setIsPlatformAdmin(false);
            }
        });
        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);
    // Sign in with email and password
    const signIn = async (email, password) => {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error)
                throw error;
            return { error: null };
        }
        catch (error) {
            console.error('Sign in error:', error);
            return { error };
        }
    };
    // Sign up new user
    const signUp = async (email, password, fullName) => {
        try {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: fullName,
                        app_origin: 'blue-app',
                        signup_timestamp: new Date().toISOString(),
                        signup_referer: typeof window !== 'undefined' ? window.location.href : '',
                    },
                },
            });
            if (error)
                throw error;
            // Create profile and default organization
            if (data.user) {
                // Create user profile first
                await supabase.from('profiles').insert({
                    id: data.user.id,
                    email: email,
                    full_name: fullName || null,
                    is_active: true,
                });
                // Create default personal organization
                const orgName = fullName ? `${fullName}'s Organization` : `${email.split('@')[0]}'s Organization`;
                const orgSlug = (fullName || email.split('@')[0])
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '') + '-' + Date.now().toString(36);
                console.log('Creating default organization:', orgName);
                const orgResult = await supabasePermissionsService.createOrganization(orgName, orgSlug, data.user.id, {
                    description: `Personal workspace for ${fullName || email}`,
                    type: 'personal',
                    plan: 'free'
                });
                if (orgResult.success && orgResult.organization) {
                    // Update user profile with organization_id
                    await supabase
                        .from('profiles')
                        .update({ organization_id: orgResult.organization.id })
                        .eq('id', data.user.id);
                    console.log('✅ Default organization created and assigned:', orgResult.organization.id);
                }
                else {
                    console.error('⚠️ Failed to create default organization:', orgResult.error);
                }
            }
            return { error: null };
        }
        catch (error) {
            console.error('Sign up error:', error);
            return { error };
        }
    };
    // Sign out
    const signOut = async () => {
        try {
            await supabase.auth.signOut();
            setUser(null);
            setUserProfile(null);
            setSession(null);
            setIsEcosystemAdmin(false);
            setIsPlatformAdmin(false);
        }
        catch (error) {
            console.error('Sign out error:', error);
        }
    };
    // Reset password - sends password reset email
    const resetPassword = async (email) => {
        try {
            // Determine the correct app URL for password reset
            // Priority: env variable > localhost:4203 (dev) > current origin (prod)
            let resetUrl;
            if (process.env.REACT_APP_URL || process.env.NEXT_PUBLIC_APP_URL) {
                // Use environment variable if set
                resetUrl = `${process.env.REACT_APP_URL || process.env.NEXT_PUBLIC_APP_URL}/reset-password`;
            }
            else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                // Development: use localhost:4203
                resetUrl = 'http://localhost:4203/reset-password';
            }
            else if (window.location.port === '4203') {
                // Already on blue-app (port 4203)
                resetUrl = `${window.location.origin}/reset-password`;
            }
            else {
                // Production or other: try to construct blue-app URL
                // For now, default to app subdomain or same origin
                const blueAppUrl = window.location.hostname.includes('cyclesofwealth.com')
                    ? 'https://app.cyclesofwealth.com' // Adjust this to your actual blue-app production domain
                    : window.location.origin;
                resetUrl = `${blueAppUrl}/reset-password`;
            }
            console.log('🔐 Password reset redirect URL:', resetUrl);
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: resetUrl,
            });
            if (error)
                throw error;
            return { error: null };
        }
        catch (error) {
            console.error('Reset password error:', error);
            return { error };
        }
    };
    // Update password - sets new password (called from reset password page)
    const updatePassword = async (newPassword) => {
        try {
            const { error } = await supabase.auth.updateUser({
                password: newPassword,
            });
            if (error)
                throw error;
            return { error: null };
        }
        catch (error) {
            console.error('Update password error:', error);
            return { error };
        }
    };
    // Refresh user profile
    const refreshUserProfile = async () => {
        if (!user)
            return;
        const profile = await supabasePermissionsService.getCurrentUserProfile();
        setUserProfile(profile);
        // Reload teams for user's organization
        if (profile?.organization_id) {
            console.log('✅ Refresh: Loading teams for organization:', profile.organization_id);
            loadTeams(profile.organization_id);
        }
        if (profile) {
            const [ecosystem, platform] = await Promise.all([
                supabasePermissionsService.isEcosystemAdmin(profile.id),
                supabasePermissionsService.isPlatformAdmin(profile.id)
            ]);
            setIsEcosystemAdmin(ecosystem);
            setIsPlatformAdmin(platform);
        }
    };
    // Check if user is account admin of specific organization
    const isAccountAdmin = (organizationId) => {
        if (!userProfile)
            return false;
        return userProfile.roles.some((role) => role.role === 'account_admin' &&
            role.context_type === 'organization' &&
            role.context_id === organizationId &&
            role.is_active);
    };
    // Generic role check
    const hasRole = (role, contextType, contextId) => {
        if (!userProfile)
            return false;
        return userProfile.roles.some((r) => r.role === role &&
            r.is_active &&
            (!contextType || r.context_type === contextType) &&
            (!contextId || r.context_id === contextId));
    };
    const value = {
        user,
        userProfile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        refreshUserProfile,
        isEcosystemAdmin,
        isPlatformAdmin,
        isAccountAdmin,
        hasRole,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
